import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCreateTournament, useTournaments } from '../api/hooks';
import { POSTER_TEMPLATES, type PosterTemplate, type TournamentRequest } from '../api/types';
import ErrorBanner from '../components/ErrorBanner';

const today = new Date();

function emptyForm(): TournamentRequest {
  const year = today.getFullYear().toString();
  return {
    name: '',
    season: year,
    startDate: today.toISOString().slice(0, 10),
    endDate: new Date(today.getTime() + 1000 * 60 * 60 * 24 * 7 * 8).toISOString().slice(0, 10),
    weeks: 8,
    description: '',
    venue: '',
    template: 'Top5',
    primaryColour: '#0B2A5B',
    accentColour: '#D6A32E',
  };
}

export default function TournamentsPage() {
  const [season, setSeason] = useState<string>('');
  const [form, setForm] = useState<TournamentRequest>(emptyForm);
  const [showForm, setShowForm] = useState(false);

  const tournaments = useTournaments(season || undefined);
  const createTournament = useCreateTournament();

  const seasons = useMemo(() => {
    const all = new Set((tournaments.data ?? []).map((t) => t.season));
    return [...all].sort().reverse();
  }, [tournaments.data]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    await createTournament.mutateAsync(form);
    setForm(emptyForm());
    setShowForm(false);
  };

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Competitions</h1>
          <p className="muted">
            Start with <strong>New poster</strong>, pick a template and fill in the details - the
            competition is created for you if it does not exist yet. Everything stays grouped by
            competition, so a league running over several weeks keeps its posters together.
          </p>
        </div>
        <div className="row row--wrap">
          <Link to="/posters/new" className="button button--primary">
            New poster
          </Link>
          <button type="button" className="button" onClick={() => setShowForm((v) => !v)}>
            {showForm ? 'Cancel' : 'New competition'}
          </button>
        </div>
      </div>

      {showForm && (
        <form className="card form" onSubmit={(e) => void submit(e)}>
          <h2>New competition</h2>
          <div className="grid grid--2">
            <label>
              Name
              <input
                required
                value={form.name}
                placeholder="Top 5"
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label>
              Season
              <input
                required
                value={form.season}
                placeholder="2026"
                onChange={(e) => setForm({ ...form, season: e.target.value })}
              />
            </label>
            <label>
              Starts
              <input
                type="date"
                required
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              />
            </label>
            <label>
              Ends
              <input
                type="date"
                required
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              />
            </label>
            <label>
              Weeks
              <input
                type="number"
                min={1}
                max={52}
                value={form.weeks}
                onChange={(e) => setForm({ ...form, weeks: Number(e.target.value) })}
              />
            </label>
            <label>
              Home venue
              <input
                value={form.venue ?? ''}
                placeholder="Burnhead"
                onChange={(e) => setForm({ ...form, venue: e.target.value })}
              />
            </label>
            <label>
              Poster layout
              <select
                value={form.template ?? 'Top5'}
                onChange={(e) => setForm({ ...form, template: e.target.value as PosterTemplate })}
              >
                {POSTER_TEMPLATES.map((template) => (
                  <option key={template.value} value={template.value}>
                    {template.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Main colour
              <input
                type="color"
                value={form.primaryColour ?? '#0B2A5B'}
                onChange={(e) => setForm({ ...form, primaryColour: e.target.value })}
              />
            </label>
            <label>
              Accent colour
              <input
                type="color"
                value={form.accentColour ?? '#D6A32E'}
                onChange={(e) => setForm({ ...form, accentColour: e.target.value })}
              />
            </label>
            <label className="span-2">
              Description
              <textarea
                rows={2}
                value={form.description ?? ''}
                placeholder="Thursday evening Top 5 league, played over eight weeks."
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </label>
          </div>

          <ErrorBanner error={createTournament.error} />

          <button type="submit" className="button button--primary" disabled={createTournament.isPending}>
            {createTournament.isPending ? 'Creating...' : 'Create competition'}
          </button>
        </form>
      )}

      <div className="toolbar">
        <label className="inline">
          Season
          <select value={season} onChange={(e) => setSeason(e.target.value)}>
            <option value="">All seasons</option>
            {seasons.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
      </div>

      <ErrorBanner error={tournaments.error} />

      {tournaments.isLoading && <p className="muted">Loading competitions...</p>}

      {tournaments.data?.length === 0 && (
        <div className="card empty">
          <h2>No competitions yet</h2>
          <p className="muted">
            Hit <strong>New poster</strong> to make your first one - it creates the competition as it goes.
          </p>
        </div>
      )}

      <div className="grid grid--cards">
        {tournaments.data?.map((tournament) => (
          <Link key={tournament.id} to={`/tournaments/${tournament.id}`} className="card card--link">
            <div
              className="card__accent"
              style={{ background: `linear-gradient(${tournament.primaryColour}, ${tournament.accentColour})` }}
            />
            <h2>{tournament.name}</h2>
            <p className="muted">
              {tournament.season} &middot; {tournament.weeks} week{tournament.weeks === 1 ? '' : 's'} &middot;{' '}
              {tournament.status}
            </p>
            <p className="muted small">
              {new Date(tournament.startDate).toLocaleDateString('en-GB')} to{' '}
              {new Date(tournament.endDate).toLocaleDateString('en-GB')}
            </p>
            {tournament.description && <p className="clamp">{tournament.description}</p>}
          </Link>
        ))}
      </div>
    </div>
  );
}
